import React from 'react';

export type CropSourceBadgeLabel = 'S' | 'F' | 'C' | 'A' | 'O';
export type CropOrientationBadgeLabel = 'F' | 'nF';

const BADGE_FILL_OPACITY_MULTIPLIER = 0.86;

export function softenBadgeFill(color: string): string {
  const rgbaMatch = color.match(
    /^rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([0-9]*\.?[0-9]+)\s*\)$/i
  );
  if (rgbaMatch) {
    const [, r, g, b, alpha] = rgbaMatch;
    const nextAlpha = Math.max(0, Math.min(1, Number(alpha) * BADGE_FILL_OPACITY_MULTIPLIER));
    return `rgba(${r}, ${g}, ${b}, ${nextAlpha.toFixed(3).replace(/0+$/, '').replace(/\.$/, '')})`;
  }

  const rgbMatch = color.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i);
  if (rgbMatch) {
    const [, r, g, b] = rgbMatch;
    return `rgba(${r}, ${g}, ${b}, ${BADGE_FILL_OPACITY_MULTIPLIER})`;
  }

  return color;
}

export function getCropSourceBadgeLabel(source?: string | null): CropSourceBadgeLabel {
  if (source === 'scan') return 'S';
  if (source === 'forge') return 'F';
  if (source === 'control') return 'C';
  if (source === 'at') return 'A';
  return 'O';
}

export function getCropSourceBadgeStyles(label: CropSourceBadgeLabel): React.CSSProperties {
  if (label === 'S') {
    return {
      background: softenBadgeFill('rgba(63, 185, 80, 0.92)'),
      border: '1px solid rgba(164, 239, 176, 0.55)',
      color: '#f3fff5',
    };
  }
  if (label === 'F') {
    return {
      background: softenBadgeFill('rgba(139, 92, 246, 0.92)'),
      border: '1px solid rgba(196, 181, 253, 0.6)',
      color: '#faf5ff',
    };
  }
  if (label === 'A') {
    return {
      background: softenBadgeFill('rgba(88, 166, 255, 0.92)'),
      border: '1px solid rgba(145, 193, 255, 0.55)',
      color: '#f3f9ff',
    };
  }
  return {
    background: softenBadgeFill('rgba(107, 114, 128, 0.92)'),
    border: '1px solid rgba(209, 213, 219, 0.45)',
    color: '#f9fafb',
  };
}

export function getCropOrientationBadgeLabel(
  orientation?: string | null
): CropOrientationBadgeLabel {
  return orientation === 'not-front' ? 'nF' : 'F';
}

export function getCropOrientationBadgeStyles(
  label: CropOrientationBadgeLabel
): React.CSSProperties {
  if (label === 'F') {
    return {
      background: softenBadgeFill('rgba(63, 185, 80, 0.92)'),
      border: '1px solid rgba(164, 239, 176, 0.55)',
      color: '#f3fff5',
    };
  }
  return {
    background: softenBadgeFill('rgba(107, 114, 128, 0.92)'),
    border: '1px solid rgba(209, 213, 219, 0.45)',
    color: '#f9fafb',
  };
}

export function CropStatusBadge({
  label,
  size,
  style,
  testId,
}: {
  label: CropSourceBadgeLabel | CropOrientationBadgeLabel;
  size: number;
  style?: React.CSSProperties;
  testId?: string;
}) {
  const isNonFront = label === 'nF';
  return (
    <div
      data-testid={testId}
      style={{
        minWidth: isNonFront
          ? Math.max(15, Math.round(size * 0.28))
          : Math.max(10, Math.round(size * 0.19)),
        height: Math.max(10, Math.round(size * 0.19)),
        padding: isNonFront ? '0 3px' : '0 2px',
        borderRadius: 999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        fontFamily: 'var(--font-mono)',
        fontSize: Math.max(7, Math.round(size * 0.11)),
        fontWeight: 700,
        lineHeight: 1,
        ...style,
      }}
    >
      {label}
    </div>
  );
}
